import {
  evaluateGroupEntry,
  stripHiddenEntryValues,
} from "@/components/features/FormBuilder/utils/evaluateLogic";
import type { FormQuestion } from "@/components/features/FormBuilder/types/form.types";
import type { FormLogicContext } from "@/components/features/FormBuilder/data/contextTriggers";
import { formatValueWithUnit } from "@/components/features/FormBuilder/utils/currencyUnit";

const OTHER_SENTINEL = "__other__";
const ROW_VALUE_MAX_LENGTH = 60;
const REF_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

type ExprAst =
  | { type: "num"; value: number }
  | { type: "ref"; id: string }
  | { type: "neg"; expr: ExprAst }
  | { type: "bin"; op: "+" | "-" | "*" | "/"; left: ExprAst; right: ExprAst };

type TemplateNode =
  | { type: "text"; value: string }
  | { type: "expr"; ast: ExprAst }
  | { type: "segment"; children: TemplateNode[] };

interface Token {
  type: "num" | "ref" | "op" | "lparen" | "rparen";
  value: string;
}

function tokenizeExpr(src: string): Token[] | null {
  const tokens: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (ch === "(") {
      tokens.push({ type: "lparen", value: ch });
      i++;
      continue;
    }
    if (ch === ")") {
      tokens.push({ type: "rparen", value: ch });
      i++;
      continue;
    }
    if (ch === "+" || ch === "-" || ch === "*" || ch === "/") {
      tokens.push({ type: "op", value: ch });
      i++;
      continue;
    }
    if (ch === "<") {
      const end = src.indexOf(">", i + 1);
      if (end === -1) return null;
      const id = src.slice(i + 1, end);
      if (!REF_ID_PATTERN.test(id)) return null;
      tokens.push({ type: "ref", value: id });
      i = end + 1;
      continue;
    }
    if (/[0-9.]/.test(ch)) {
      let j = i;
      while (j < src.length && /[0-9.]/.test(src[j])) j++;
      const numStr = src.slice(i, j);
      if (!/^\d+(\.\d+)?$/.test(numStr)) return null;
      tokens.push({ type: "num", value: numStr });
      i = j;
      continue;
    }
    return null;
  }
  return tokens;
}

function parseExprTokens(tokens: Token[]): ExprAst | null {
  let pos = 0;
  const peek = () => tokens[pos];
  const consume = () => tokens[pos++];

  function parseExpression(): ExprAst | null {
    let left = parseTerm();
    if (!left) return null;
    let next = peek();
    while (next && next.type === "op" && (next.value === "+" || next.value === "-")) {
      const op = consume().value as "+" | "-";
      const right = parseTerm();
      if (!right) return null;
      left = { type: "bin", op, left, right };
      next = peek();
    }
    return left;
  }

  function parseTerm(): ExprAst | null {
    let left = parseFactor();
    if (!left) return null;
    let next = peek();
    while (next && next.type === "op" && (next.value === "*" || next.value === "/")) {
      const op = consume().value as "*" | "/";
      const right = parseFactor();
      if (!right) return null;
      left = { type: "bin", op, left, right };
      next = peek();
    }
    return left;
  }

  function parseFactor(): ExprAst | null {
    const tok = peek();
    if (!tok) return null;
    if (tok.type === "op" && tok.value === "-") {
      consume();
      const inner = parseFactor();
      if (!inner) return null;
      return { type: "neg", expr: inner };
    }
    if (tok.type === "num") {
      consume();
      return { type: "num", value: Number(tok.value) };
    }
    if (tok.type === "ref") {
      consume();
      return { type: "ref", id: tok.value };
    }
    if (tok.type === "lparen") {
      consume();
      const inner = parseExpression();
      if (!inner) return null;
      const closing = peek();
      if (!closing || closing.type !== "rparen") return null;
      consume();
      return inner;
    }
    return null;
  }

  const ast = parseExpression();
  if (!ast || pos !== tokens.length) return null;
  return ast;
}

function parseExpressionString(src: string): ExprAst | null {
  const tokens = tokenizeExpr(src);
  if (!tokens || tokens.length === 0) return null;
  return parseExprTokens(tokens);
}

function findMatchingBracket(src: string, openIdx: number): number {
  let depth = 0;
  for (let i = openIdx; i < src.length; i++) {
    if (src[i] === "[") depth++;
    else if (src[i] === "]") {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function parseTemplate(src: string): TemplateNode[] {
  const nodes: TemplateNode[] = [];
  let buf = "";
  let i = 0;

  const flush = () => {
    if (buf) {
      nodes.push({ type: "text", value: buf });
      buf = "";
    }
  };

  while (i < src.length) {
    const ch = src[i];

    if (ch === "{") {
      const end = src.indexOf("}", i + 1);
      if (end === -1) {
        buf += ch;
        i++;
        continue;
      }
      const inner = src.slice(i + 1, end);
      const ast = parseExpressionString(inner);
      flush();
      if (ast) nodes.push({ type: "expr", ast });
      i = end + 1;
      continue;
    }

    if (ch === "[") {
      const closeIdx = findMatchingBracket(src, i);
      if (closeIdx === -1) {
        buf += ch;
        i++;
        continue;
      }
      flush();
      const innerSrc = src.slice(i + 1, closeIdx);
      nodes.push({ type: "segment", children: parseTemplate(innerSrc) });
      i = closeIdx + 1;
      continue;
    }

    buf += ch;
    i++;
  }

  flush();
  return nodes;
}

function collectAstRefs(ast: ExprAst, out: string[]) {
  if (ast.type === "num") return;
  if (ast.type === "ref") {
    out.push(ast.id);
    return;
  }
  if (ast.type === "neg") {
    collectAstRefs(ast.expr, out);
    return;
  }
  collectAstRefs(ast.left, out);
  collectAstRefs(ast.right, out);
}

function collectDirectRefs(nodes: TemplateNode[]): string[] {
  const ids: string[] = [];
  for (const node of nodes) {
    if (node.type === "expr") collectAstRefs(node.ast, ids);
  }
  return ids;
}

function findField(group: FormQuestion, id: string): FormQuestion | undefined {
  return (group.fields || []).find((field) => field.id === id);
}

function isValueEmpty(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

function hasResolvableValue(
  group: FormQuestion,
  item: Record<string, unknown>,
  id: string,
): boolean {
  const field = findField(group, id);
  if (!field) return false;
  return !isValueEmpty(item[id]);
}

function formatChoiceValue(
  field: FormQuestion,
  item: Record<string, unknown>,
  raw: unknown,
): string | null {
  const values = Array.isArray(raw) ? raw : [raw];
  const labels: string[] = [];

  for (const value of values) {
    if (value === OTHER_SENTINEL) {
      const otherText = item[`${field.id}__other`];
      if (otherText !== undefined && otherText !== null && String(otherText).trim() !== "") {
        labels.push(String(otherText).trim());
      }
      continue;
    }
    const option = (field.options || []).find((o) => o.id === value);
    if (option) labels.push(option.value);
    else if (typeof value === "string" && value) labels.push(value);
  }

  if (labels.length === 0) return null;
  return labels.join(", ");
}

function formatNumber(n: number): string {
  let s = n.toFixed(2);
  if (s.includes(".")) {
    s = s.replace(/0+$/, "");
    s = s.replace(/\.$/, "");
  }
  return s === "-0" ? "0" : s;
}

function formatSingleRefValue(
  group: FormQuestion,
  item: Record<string, unknown>,
  id: string,
): string | null {
  const field = findField(group, id);
  if (!field) return null;
  const raw = item[id];

  switch (field.type) {
    case "radio":
    case "dropdown":
    case "checkbox":
      if (isValueEmpty(raw)) return null;
      return formatChoiceValue(field, item, raw);
    case "number": {
      if (isValueEmpty(raw)) return null;
      return formatValueWithUnit(raw, field.unit);
    }
    case "acknowledgement":
      return raw === true ? "Yes" : null;
    default:
      if (isValueEmpty(raw)) return null;
      return String(raw).trim() || null;
  }
}

function resolveNumber(item: Record<string, unknown>, ast: ExprAst): number {
  switch (ast.type) {
    case "num":
      return ast.value;
    case "ref": {
      const raw = item[ast.id];
      if (isValueEmpty(raw) || Array.isArray(raw)) return NaN;
      return Number(raw);
    }
    case "neg": {
      const value = resolveNumber(item, ast.expr);
      return Number.isNaN(value) ? NaN : -value;
    }
    case "bin": {
      const left = resolveNumber(item, ast.left);
      const right = resolveNumber(item, ast.right);
      if (Number.isNaN(left) || Number.isNaN(right)) return NaN;
      switch (ast.op) {
        case "+":
          return left + right;
        case "-":
          return left - right;
        case "*":
          return left * right;
        case "/":
          return right === 0 ? NaN : left / right;
      }
    }
  }
}

function renderExprNode(group: FormQuestion, item: Record<string, unknown>, ast: ExprAst): string {
  if (ast.type === "ref") {
    return formatSingleRefValue(group, item, ast.id) ?? "";
  }
  const value = resolveNumber(item, ast);
  return Number.isNaN(value) ? "" : formatNumber(value);
}

function renderNodes(
  group: FormQuestion,
  item: Record<string, unknown>,
  nodes: TemplateNode[],
): string {
  let out = "";
  for (const node of nodes) {
    if (node.type === "text") {
      out += node.value;
    } else if (node.type === "expr") {
      out += renderExprNode(group, item, node.ast);
    } else {
      const refs = collectDirectRefs(node.children);
      const allResolved = refs.every((id) => hasResolvableValue(group, item, id));
      if (allResolved) out += renderNodes(group, item, node.children);
    }
  }
  return out;
}

function collapseWhitespace(s: string): string {
  return s.replace(/\s+/g, " ").trim();
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

export function renderRowTemplate(
  template: string,
  group: FormQuestion,
  item: Record<string, unknown>,
): string {
  const nodes = parseTemplate(template);
  return collapseWhitespace(renderNodes(group, item, nodes));
}

export function getRowDisplay(
  group: FormQuestion,
  item: Record<string, unknown>,
  context?: FormLogicContext,
): { title: string; subtitle: string } {
  const stripped = stripHiddenEntryValues(group, item, context);

  if (group.groupConfig?.rowTitle) {
    const title = renderRowTemplate(group.groupConfig.rowTitle, group, stripped);
    const subtitle = group.groupConfig.rowSubtitle
      ? renderRowTemplate(group.groupConfig.rowSubtitle, group, stripped)
      : "";
    return { title, subtitle };
  }

  const { visibleFields } = evaluateGroupEntry(group, item, context);
  const answeredValues: string[] = [];
  for (const field of visibleFields) {
    const value = formatSingleRefValue(group, stripped, field.id);
    if (value) answeredValues.push(truncate(value, ROW_VALUE_MAX_LENGTH));
  }

  return {
    title: answeredValues[0] || "",
    subtitle: answeredValues.slice(1).join(" · "),
  };
}

export function tokensToLabels(template: string, group: FormQuestion): string {
  return template.replace(/\{([^{}]*)\}/g, (_match, inner: string) => {
    const replaced = inner.replace(/<([A-Za-z0-9_-]+)>/g, (match, id: string) => {
      const field = findField(group, id);
      return field ? field.question : match;
    });
    return `{${replaced}}`;
  });
}

export function labelsToTokens(displayTemplate: string, group: FormQuestion): string {
  const fields = (group.fields || [])
    .filter((field) => field.question)
    .slice()
    .sort((a, b) => b.question.length - a.question.length);

  return displayTemplate.replace(/\{([^{}]*)\}/g, (_match, inner: string) => {
    let result = "";
    let i = 0;
    while (i < inner.length) {
      const match = fields.find((field) => inner.startsWith(field.question, i));
      if (match) {
        result += `<${match.id}>`;
        i += match.question.length;
      } else {
        result += inner[i];
        i++;
      }
    }
    return `{${result}}`;
  });
}
